import TextField from "@mui/material/TextField";
import styled, { css } from "styled-components";
import { colors } from "healthex-common/colors";
import { fontSizes } from "healthex-common/sizes";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import React from "react";
import { slugify } from "healthex-common/utils";
import EditIcon from "@mui/icons-material/Edit";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
export function withExplicitProps(styledComponent, ...props) {
    return styledComponent.withConfig({
        shouldForwardProp: (prop, defaultValidatorFn) => !props.includes(prop.toString()) && defaultValidatorFn(prop),
    });
}
export function srcAttr(src) {
    return () => ({ src });
}
export function multilineEllipsis(lines) {
    return css `
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${lines};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
    `;
}
export function makeBaseStyles(units, buttonFontSize) {
    const IconTextPair = css `
        display: flex;
        gap: ${units(3)};
        align-items: center;
    `;
    const BaseTextButton = styled(Button).attrs({ variant: "text" }) `
        height: 40px;
        padding: 0 16px;
    `;
    const BaseOutlinedTextButton = styled(BaseTextButton).attrs({ variant: "outlined" }) ``;
    const BaseContainedTextButton = styled(BaseTextButton).attrs({ variant: "contained" }) ``;
    const BaseBlueTextButton = styled(BaseTextButton).attrs({ variant: "text", color: "info" }) ``;
    const DeleteButton = styled(IconButton).attrs({
        size: "medium",
        children: React.createElement(DeleteOutlinedIcon),
    }) ``;
    const EditButton = styled(IconButton).attrs({
        size: "medium",
        children: React.createElement(EditIcon),
    }) ``;
    const BaseLinkWithExplicitProps = withExplicitProps(styled.a, "analyticsId");
    const BaseLink = BaseLinkWithExplicitProps.attrs((props) => ({
        "data-heap-id": props.analyticsId ? slugify(props.analyticsId) : undefined,
    })) `
        color: ${colors.navy_main};
        cursor: pointer;
        text-decoration: underline;

        &:hover {
            color: ${colors.navy_dark};
        }

        ${(props) => props.disabled &&
        css `
                color: ${colors.black_30};
            `}
    `;
    const conditionalHighlightedBorder = () => css `
        border-color: ${colors.sunset_main};
        border-width: 2px;
        transition: border-color 0.3s, border-width 0.3s;
    `;
    const HXTextField = styled(TextField).attrs(() => ({
        size: "small",
    })) `
        ${(props) => props.highlighted &&
        css `
                /* Conditional Border Styling */
                & .MuiOutlinedInput-root {
                    fieldset {
                        ${conditionalHighlightedBorder}
                    }

                    &:hover fieldset {
                        ${conditionalHighlightedBorder}
                    }

                    &.Mui-focused fieldset {
                        ${conditionalHighlightedBorder}
                    }
                }
            `}
    `;
    const HXCheckboxBase = (props) => {
        const { analyticsId, error, control, ...rest } = props;
        const controlEl = control !== null && control !== void 0 ? control : (analyticsId
            ? React.createElement(Checkbox, { inputProps: { "data-heap-id": slugify(analyticsId) } })
            : React.createElement(Checkbox));
        return React.createElement(FormControlLabel, {
            control: controlEl,
            labelPlacement: "end",
            ...rest,
        });
    };
    const HXCheckbox = styled(HXCheckboxBase) `
        .MuiSvgIcon-root {
            width: 24px;
            height: 24px;
        }

        &.MuiFormControlLabel-labelPlacementEnd {
            margin-left: 0; // There's a default -11 left-margin
        }

        ${(props) => props.error &&
        css `
                .MuiSvgIcon-root {
                    color: ${colors.tulip_main};
                }

                .MuiFormControlLabel-label {
                    color: ${colors.tulip_main};
                }
            `}
    `;
    const HXCheckbox2 = styled(HXCheckbox) `
        &.MuiFormControlLabel-labelPlacementEnd {
            align-items: flex-start;
        }

        .MuiCheckbox-root {
            padding: 0px;
            margin: 0px 9px 9px 0px;
        }
    `;
    const HXCheckboxInline = styled(HXCheckbox) `
        align-self: flex-start;
    `;
    const HXRadioButton = styled(FormControlLabel).attrs({
        control: React.createElement(Radio),
        labelPlacement: "end",
    }) `
        .MuiSvgIcon-root {
            width: 24px;
            height: 24px;
        }

        &.MuiFormControlLabel-labelPlacementEnd {
            margin-left: 0; // There's a default -11 left-margin
        }

        ${(props) => props.error &&
        css `
                .MuiSvgIcon-root {
                    color: ${colors.tulip_main};
                }

                .MuiFormControlLabel-label {
                    color: ${colors.tulip_main};
                }
            `}
    `;
    const HXNumericTextField = styled(HXTextField) `
        width: 100px;

        & .MuiInputAdornment-root p {
            font-size: ${fontSizes.SMALL};
        }
    `;
    return {
        IconTextPair,
        BaseTextButton,
        BaseOutlinedTextButton,
        BaseContainedTextButton,
        BaseBlueTextButton,
        BaseLink,
        DeleteButton,
        EditButton,
        HXTextField,
        HXCheckbox,
        HXCheckbox2,
        HXCheckboxInline,
        HXRadioButton,
        HXNumericTextField,
    };
}
export const { IconTextPair, BaseTextButton, BaseOutlinedTextButton, BaseContainedTextButton, BaseBlueTextButton, BaseLink, DeleteButton, EditButton, HXTextField, HXCheckbox, HXCheckbox2, HXCheckboxInline, HXRadioButton, HXNumericTextField, } = makeBaseStyles((value) => `${value}px`, fontSizes.MICRO);
